import {
  Box,
  Heading,
  Stack,
  Link as ChakraLink,
  Text,
  Flex,
  ClientOnly,
} from '@chakra-ui/react';
import {Gallery} from 'react-photoswipe-gallery';
import Image from '../Image';
import ImmichPhotos from './ImmichPhotos';
import {type ImmichAsset} from '../../utils/immich';
import Countdown from 'react-countdown';
import {Link} from '@tanstack/react-router';
import {DirectusImage, imageUrl} from '../../utils/directusImage';
import MarkdownText from '../MarkdownText';
import {Markdown} from '../../utils/markdownText';

export default function Event({
  event,
}: {
  event: {
    id: string;
    start: Date;
    end: Date;
    name: string;
    description: Markdown | null;
    location: string | null;
    poster: DirectusImage | null;
    bandPlayings: Array<{
      name: string;
    }>;
    immich?: {
      slug: string;
      assets: ImmichAsset[];
      totalCount: number;
    } | null;
  };
}) {
  return (
    <>
      <Stack
        direction={['column', 'row']}
        gap="5"
        mt="5"
        align={['center', 'flex-start']}
      >
        {event.poster && (
          <Gallery withCaption>
            <Image
              w="200px"
              flexShrink="0"
              src={imageUrl(event.poster.id, {width: 200})}
              original={imageUrl(event.poster.id, {width: 1600})}
              originalHeight={event.poster.height}
              originalWidth={event.poster.width}
              alt={`${event.name} Poster`}
              caption={
                event.poster.copyright
                  ? `Gestaltung: ${event.poster.copyright}`
                  : undefined
              }
            />
          </Gallery>
        )}
        <Box w="100%">
          {event.start.getTime() > new Date().getTime() && (
            <Flex
              textAlign="center"
              fontWeight="bold"
              alignItems="center"
              flexDirection="row"
              w="100%"
              bg="brand.900"
              color="white"
              borderRadius="lg"
              boxShadow="lg"
              maxW="500px"
              pb="2"
              h={['80px', '110px']}
              mx="auto"
              mt="3"
              mb="3"
              transform="rotate(1deg)"
            >
              <ClientOnly>
                <Countdown
                  date={event.start}
                  renderer={({days, hours, minutes, seconds}) => (
                    <>
                      <Box flexBasis={1} flexGrow={1}>
                        <Text
                          fontSize={['xx-large', 'xxx-large']}
                          fontFamily="Shrimp"
                          mb="-3"
                        >
                          {days}
                        </Text>
                        <Text>Tage</Text>
                      </Box>
                      <Box flexBasis={1} flexGrow={1}>
                        <Text
                          fontSize={['xx-large', 'xxx-large']}
                          fontFamily="Shrimp"
                          mb="-3"
                        >
                          {hours}
                        </Text>
                        <Text>Stunden</Text>
                      </Box>
                      <Box flexBasis={1} flexGrow={1}>
                        <Text
                          fontSize={['xx-large', 'xxx-large']}
                          fontFamily="Shrimp"
                          mb="-3"
                        >
                          {minutes}
                        </Text>
                        <Text>Minuten</Text>
                      </Box>
                      <Box flexBasis={1} flexGrow={1}>
                        <Text
                          fontSize={['xx-large', 'xxx-large']}
                          fontFamily="Shrimp"
                          mb="-3"
                        >
                          {seconds}
                        </Text>
                        <Text>Sekunden</Text>
                      </Box>
                    </>
                  )}
                />
              </ClientOnly>
            </Flex>
          )}
          {event.description && <MarkdownText {...event.description} />}
          {event.bandPlayings.length > 0 && (
            <>
              <Heading as="h3" size="md" mb="2">
                Lineup
              </Heading>
              <Box>
                mit{' '}
                {event.bandPlayings
                  .slice(0, 12)
                  .map((b) => b.name)
                  .join(', ')}{' '}
                <ChakraLink asChild display="inline">
                  <Link
                    to="/lineup/$year"
                    params={{year: event.start.getFullYear().toString()}}
                  >
                    {event.bandPlayings.length > 12 ? (
                      <>
                        und {event.bandPlayings.length - 12} weiteren
                        Bands&hellip;
                      </>
                    ) : (
                      <>Lineup</>
                    )}
                  </Link>
                </ChakraLink>
              </Box>
            </>
          )}
        </Box>
      </Stack>
      {event.immich && event.immich.assets.length > 0 && (
        <Box w="100%" mt="10">
          <Heading as="h3" size="lg" mt="4" mb="2" id="fotos">
            Fotos
          </Heading>
          <ImmichPhotos
            eventId={event.id}
            slug={event.immich.slug}
            assets={event.immich.assets}
            totalCount={event.immich.totalCount}
          />
        </Box>
      )}
    </>
  );
}
